/* eslint-disable no-param-reassign */
import React, { PureComponent } from 'react';
import { Input, Form, Select, Checkbox, Radio, Tree, Card } from 'antd';
import memoize from 'lodash/memoize';
import map from 'lodash/map';
import every from 'lodash/every';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import SelectActions from '../../../component/SelectActions';
import nodes from '../HubSave/nodes';
import {
  renderTreeNodes,
  trimTree,
  getRequiredFields,
  getDependencies,
  getLeaves,
  getLabels,
} from '../../../utils/checkBoxTreeUtils';

const { Option } = Select;

const FormItem = props => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Form.Item labelCol={{ span: 4 }} wrapperCol={{ span: 6 }} {...props} />
);

const labels = getLabels(nodes);

class HubForm extends PureComponent {
  requiredFields = getRequiredFields(nodes);

  dependencies = getDependencies(nodes);

  trimTree = memoize(
    clusterConfig => trimTree(nodes, clusterConfig.data.checked),
    ({ id }) => id
  );

  onNameChange = e => {
    const { changeHubProp } = this.props;
    changeHubProp({ name: e.target.value });
  };

  onConfigTypeChange = e => {
    const { changeHubProp } = this.props;
    changeHubProp({ configType: e.target.value });
  };

  onHubConfigChange = hubConfigId => {
    const { changeHubProp } = this.props;
    changeHubProp({
      hubConfigId,
      checked: this.requiredFields,
      expanded: map(nodes, 'key'),
    });
  };

  onHubChange = copyHubId => {
    const { changeHubProp } = this.props;
    changeHubProp({ copyHubId });
  };

  onCheck = checked => {
    checked = union(checked, this.requiredFields);
    checked = intersection(getLeaves(nodes), checked);
    checked = checked.filter(module => {
      const dependencies = this.dependencies[module];
      if (!dependencies) return true;
      return every(dependencies, dep => checked.includes(dep));
    });
    const { changeHubProp } = this.props;
    changeHubProp({ checked });
  };

  onExpand = expanded => {
    const { changeHubProp } = this.props;
    changeHubProp({ expanded });
  };

  onCopyUsersChange = e => {
    const { changeHubProp } = this.props;
    changeHubProp({ copyUsers: e.target.checked });
  };

  onIdentifierChange = e => {
    const { changeHubProp } = this.props;
    changeHubProp({ identifier: e.target.value });
  };

  onHubConfigFilter = (input, option) => {
    const { hubConfigs } = this.props;
    const hubConfig = hubConfigs.find(c => c.id === parseInt(option.key, 10));
    return hubConfig.name.toLowerCase().includes(input.toLowerCase());
  };

  onHubFilter = (input, option) => {
    const { hubs } = this.props;
    const hub = hubs.find(c => c.id === parseInt(option.key, 10));
    return hub.name.toLowerCase().includes(input.toLowerCase());
  };

  renderConfigSelectDropdown = () => {
    const {
      hubTabKey,
      newHubs,
      hubs,
      hubsLoading,
      hubConfigs,
      hubConfigsLoading,
    } = this.props;
    const { configType, copyHubId, hubConfigId, copyUsers } = newHubs.find(
      h => h.key === hubTabKey
    );
    if (configType === 'hubConfig')
      return (
        <FormItem label="hub configuration">
          <Select
            style={{ width: 300 }}
            showSearch
            placeholder="Select Hub Config"
            onChange={this.onHubConfigChange}
            filterOption={this.onHubConfigFilter}
            loading={hubConfigsLoading}
            value={hubConfigId}
          >
            {hubConfigs
              .filter(h => h.status === 'done')
              .map(config => (
                <Option key={config.id} value={config.id}>
                  {config.name}
                </Option>
              ))}
          </Select>
        </FormItem>
      );
    else if (configType === 'copy')
      return (
        <div>
          <FormItem label="Hub to copy">
            <Select
              style={{ width: 300 }}
              showSearch
              placeholder="Select Hub"
              onChange={this.onHubChange}
              filterOption={this.onHubFilter}
              loading={hubsLoading}
              value={copyHubId}
            >
              {hubs.map(hub => (
                <Option key={hub.id} value={hub.id}>
                  {hub.name}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem label="Copy Users and Content">
            <Checkbox value={copyUsers} onChange={this.onCopyUsersChange} />
          </FormItem>
        </div>
      );
    return null;
  };

  onSelectAll = () => {
    const { hubConfigs, hubTabKey, newHubs } = this.props;
    const { hubConfigId } = newHubs.find(h => h.key === hubTabKey);
    const hubConfig = hubConfigs.find(cc => cc.id === hubConfigId);
    this.onCheck(getLeaves(this.trimTree(hubConfig)));
  };

  onDeselectAll = () => this.onCheck([]);

  renderHubConfigTree = () => {
    const { hubConfigs, hubTabKey, newHubs } = this.props;
    const { configType, hubConfigId, checked, expanded } = newHubs.find(
      h => h.key === hubTabKey
    );

    if (configType === 'hubConfig' && hubConfigId) {
      const hubConfig = hubConfigs.find(cc => cc.id === hubConfigId);
      return (
        <Card
          title={
            <div style={{ fontSize: '14px', color: 'rgba(0,0,0,0.45)' }}>
              Select settings to import
            </div>
          }
        >
          <SelectActions
            onSelectAll={this.onSelectAll}
            onDeselectAll={this.onDeselectAll}
          />
          <Tree
            checkable
            onExpand={this.onExpand}
            onCheck={this.onCheck}
            expandedKeys={expanded}
            checkedKeys={checked}
            selectedKeys={[]}
          >
            {renderTreeNodes(this.trimTree(hubConfig), labels)}
          </Tree>
        </Card>
      );
    }
    return null;
  };

  render() {
    const { hubTabKey, newHubs, selectedEnabledGroup } = this.props;
    const { name, identifier, configType } = newHubs.find(
      h => h.key === hubTabKey
    );

    return (
      <div>
        <Form layout="horizontal" labelAlign="left">
          <FormItem label="Hub Name">
            <Input
              placeholder="University Connect"
              value={name}
              onChange={this.onNameChange}
            />
          </FormItem>
          <FormItem
            label="Launch Group"
            help="Update selected cluster launch group to reflect changes here"
          >
            <Input
              placeholder="peoplegrove"
              value={selectedEnabledGroup?.name}
              disabled
            />
          </FormItem>
          <FormItem
            label="Hub Identifier (Optional)"
            help="Should be a combination of alphabets, numbers or underscore"
          >
            <Input
              placeholder="university-connect"
              value={identifier}
              onChange={this.onIdentifierChange}
            />
          </FormItem>
          <FormItem label="Hub Configuration" wrapperCol={{ span: 12 }}>
            <Radio.Group value={configType} onChange={this.onConfigTypeChange}>
              {!selectedEnabledGroup?.forStandard && (
                <Radio value="none">None</Radio>
              )}
              <Radio value="copy">Copy Hub</Radio>
              <Radio value="hubConfig">Use Hub Config</Radio>
            </Radio.Group>
          </FormItem>
        </Form>
        {this.renderConfigSelectDropdown()}
        {this.renderHubConfigTree()}
      </div>
    );
  }
}
export default HubForm;
